import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { ORDER_TYPES, UPLOAD_BASE, fmtDateTH, fmtDateTimeTH } from './aoShared'

// คำสั่ง/ประกาศที่เกี่ยวข้องกับฉัน — ครูทุกคน กดรับทราบได้
export default function MyOrders() {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get('/api/ao/my/orders')
      if (res.data.success) setOrders(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const handleAck = async (o) => {
    const tt = ORDER_TYPES[o.od_type] || ORDER_TYPES.order
    const result = await Swal.fire({
      icon: 'question',
      title: `รับทราบ${tt.label}?`,
      text: `${o.od_full_no} — ${o.od_title}`,
      showCancelButton: true,
      confirmButtonText: 'รับทราบ',
      cancelButtonText: 'ปิด',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.post(`/api/ao/my/orders/${o.oa_id}/ack`)
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'รับทราบไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const pending = orders.filter(o => !o.oa_ack)
  const acked = orders.filter(o => o.oa_ack)

  const OrderCard = ({ o }) => {
    const tt = ORDER_TYPES[o.od_type] || ORDER_TYPES.order
    return (
      <div className="border rounded p-3 h-100 d-flex flex-column">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <Badge bg={tt.bg} className="me-1">{tt.label}</Badge>
            <span className="fw-semibold">{o.od_title}</span>
          </div>
          {o.oa_ack
            ? <Badge bg="success">รับทราบแล้ว</Badge>
            : <Badge bg="warning">รอรับทราบ</Badge>}
        </div>
        <div className="mt-2 pt-2 border-top fs-sm flex-grow-1">
          <div><span className="text-muted">เลขที่:</span> {o.od_full_no} · {fmtDateTH(o.od_date)}</div>
          {o.oa_role && <div><span className="text-muted">บทบาท:</span> {o.oa_role}</div>}
          {o.od_detail && <div className="text-truncate" title={o.od_detail}><span className="text-muted">รายละเอียด:</span> {o.od_detail}</div>}
          {o.oa_ack === 1 && (
            <div className="text-muted fs-xs mt-1">รับทราบเมื่อ {fmtDateTimeTH(o.oa_ack_at)}</div>
          )}
        </div>
        <div className="mt-2 pt-2 border-top d-flex gap-2">
          {o.od_file && (
            <Button size="sm" variant="outline-secondary" className="flex-grow-1"
              href={`${UPLOAD_BASE}/uploads/ao_orders/${o.od_file}`} target="_blank" rel="noreferrer">
              <i className="ri-attachment-2 me-1" />เปิดเอกสาร
            </Button>
          )}
          {!o.oa_ack && (
            <Button size="sm" variant="primary" className="flex-grow-1" onClick={() => handleAck(o)}>
              <i className="ri-check-line me-1" />รับทราบ
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-file-list-3-line me-2 text-primary" />
              คำสั่ง / ประกาศของฉัน
            </h4>
            {pending.length > 0 && <Badge bg="warning" className="fs-sm">รอรับทราบ {pending.length} ฉบับ</Badge>}
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : orders.length === 0 ? (
            <Card className="card-one"><Card.Body className="text-center text-muted py-5">ยังไม่มีคำสั่ง/ประกาศที่เกี่ยวข้องกับคุณ</Card.Body></Card>
          ) : (
            <>
              {pending.length > 0 && (
                <>
                  <h6 className="mb-2">รอรับทราบ</h6>
                  <Row className="g-3 mb-4">
                    {pending.map(o => <Col key={o.oa_id} xs={12} md={6} xl={4}><OrderCard o={o} /></Col>)}
                  </Row>
                </>
              )}
              {acked.length > 0 && (
                <>
                  <h6 className="mb-2 text-muted">รับทราบแล้ว</h6>
                  <Row className="g-3">
                    {acked.map(o => <Col key={o.oa_id} xs={12} md={6} xl={4}><OrderCard o={o} /></Col>)}
                  </Row>
                </>
              )}
            </>
          )}
        </Container>
      </div>
      <Footer />
    </>
  )
}
